import { createFileRoute } from '@tanstack/react-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { BannerMessage } from '@/common/BannerMessage';
import { LocalTabsBar } from '@/common/LocalTabsBar';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { SeriesSection } from '@/courses/SeriesSection';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { apiLanguage } from '~/i18n';
import { localeToApiLanguage } from '~/utils/languageFormat';

export const Route = createFileRoute('/_app/all-courses/')({
  component: CoursesScreen,
  beforeLoad: () => {
    return {
      pageBackground: 'white',
    };
  },
});

export default function CoursesScreen() {
  const { t } = useTranslation();
  const { me, isStudent } = useCurrentUser();
  const memberId = String(me?.id || '');

  // The INTERFACE language, not the viewer's stored account language. Legacy
  // renders and caches the public directory per interface language with no
  // per-user switch (`modules/series/template-functions.php:16,24`, cache key
  // `wbs_series_directory_html_cache?{$current_language}`), and it is a nopriv
  // page a signed-out visitor reaches. `useTranslation` re-renders this
  // component on a switch, and `language` is part of the query key, so the
  // footer switcher refetches with no extra invalidation.
  const language = localeToApiLanguage(apiLanguage());

  const { data: seriesData, isLoading } = api.listSeries.useQuery({
    query: { language, limit: 50 },
  });
  const seriesList = seriesData?.data ?? [];

  const seriesTabs = seriesList.map((s) => ({
    title: s.title,
    id: String(s.id),
  }));

  const [selectedId, setSelectedId] = React.useState<string | undefined>();

  // Derived rather than an effect: a selection made before a language switch
  // names a series id the new payload does not contain, and pinning it would
  // leave the page with a tab strip and nothing under it. The payload arrives
  // ordered (series `order`, then status, then created_at), so the fallback is
  // the first tab legacy shows.
  const selectedSeries =
    seriesList.find((s) => String(s.id) === selectedId) ?? seriesList[0];

  if (isLoading) {
    return (
      <PageContainer>
        <div style={{ margin: '9px 0 9px 0' }}>
          <TitleWithQuestion title={t('navigation.courses')} />
        </div>
        <LoadingText>{t('lessons.loading-all-courses')}</LoadingText>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div style={{ margin: '9px 0 9px 0' }}>
        <TitleWithQuestion title={t('navigation.courses')} />
      </div>
      {/* Students only, as in legacy (`templates/page-courses.php:18-28`). The
          copy is the ACF `student_viewing_notice` of the four translated
          Courses pages, now a locale key. */}
      {isStudent ? (
        <div style={{ margin: '26px 0' }}>
          <BannerMessage
            text={t('lessons.courses-directory-notice')}
            buttonText={t('lessons.view-my-progress')}
            buttonTo="/members/$memberId/course-study"
            buttonParams={{ memberId }}
          />
        </div>
      ) : null}
      {selectedSeries && (
        <div style={{ margin: '18px 0' }}>
          <LocalTabsBar
            tabs={seriesTabs}
            selectedId={String(selectedSeries.id)}
            onTabClick={(tab) => setSelectedId(tab.id)}
          />
        </div>
      )}

      {/* A series with no courses keeps its tab and renders as an empty
          section — legacy emits both (EN "Let the Bible Speak", ES
          "Suplementarios") — so this must not fall back to the loading text. */}
      {selectedSeries && <SeriesSection series={selectedSeries} />}
    </PageContainer>
  );
}

const PageContainer = styled.div`
  box-sizing: border-box;
  padding: 24px;
  width: 100%;
`;

const LoadingText = styled.div`
  color: var(--grey-600);
  font-family: var(--raleway);
  font-size: 16px;
  padding: 24px 0;
`;
